import { useState } from 'react';
import { c, z } from '../../theme';

const H = 46;                       // drawn height, in px and in viewBox units

// The month-by-month line under each Exec card, with its own range printed.
//
// InContact's months (before Genesys, 4 June 2026) are drawn DASHED and the
// Genesys months solid, because they come from two different phone systems and
// a single unbroken line would claim a continuity that is not there. The old
// page says so in a footnote and so does this.
//
// Each point carries a marker against its target: a triangle in the direction
// that is good, a dot where there is no target to judge against. The last
// point is filled, so "where we are now" is findable without counting.
//
// Hovering a month names it and gives its figure and the move from the month
// before — the line's shape says a trend turned, and the reader's next
// question is always "turned to what". Before this the only way to read a
// value off the chart was the range under it, which gives two numbers out of
// nine and does not say which months they were.
// `stretch` lets the line fill the card rather than sitting at its drawn width
// (220px) in the middle of it, which is what the Exec cards need and what the
// old page does. It comes with `axis` (the month initials underneath) and
// `markers` (a triangle per month, in the direction that is good) — all three
// default off, so the pages that had this line before are unchanged.
export default function MetricSparkline({ months, target, betterDir = 'up', fmt, rangeLabel,
  stretch = false, axis = false, markers = false }) {
  const [hover, setHover] = useState(null);
  const points = (months || []).filter((m) => m.value != null);
  if (points.length < 2) return null;

  const W = 220;
  const PAD = 4;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => PAD + (i * (W - PAD * 2)) / (points.length - 1);
  const y = (v) => H - PAD - ((v - min) / span) * (H - PAD * 2);

  // Two paths, not one: the dashed run stops where Genesys begins, and they
  // share the boundary point so the line does not break visually.
  const firstLive = points.findIndex((p) => !p.legacy);
  const legacyPts = firstLive <= 0 ? [] : points.slice(0, firstLive + 1);
  const livePts = firstLive < 0 ? [] : points.slice(firstLive);
  const pathOf = (pts, offset) => pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(offset + i).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(' ');

  // A month against the one before it. Colour says whether the move was in the
  // direction we want, which is not the same as up: an AHT that fell is green.
  //
  // `change` is the move itself and `step` is whether a marker is drawn for it.
  // They are separate because the thresholds differ: the arrows above the line
  // stay quiet under 2% and say nothing at all for a metric with no better
  // direction, which is right for a glyph that has to read at a glance. The
  // tooltip is read deliberately, one month at a time, so it reports the move
  // whatever its size — suppressing "0.4% vs Jun" there would look like a
  // month with no comparison rather than a month that barely moved.
  const change = (i) => {
    if (i === 0) return null;
    const prev = points[i - 1].value;
    const cur = points[i].value;
    if (prev == null || cur == null || prev === 0) return null;
    const pc = ((cur - prev) / Math.abs(prev)) * 100;
    const good = betterDir === 'neutral' ? null : (betterDir === 'up' ? pc > 0 : pc < 0);
    return { pc, good, prevLabel: points[i - 1].label };
  };

  const step = (i) => {
    const d = change(i);
    if (!d || d.good == null || Math.abs(d.pc) < 2) return null;   // flat enough to say nothing
    return { glyph: d.good ? '▲' : '▼', color: d.good ? c.successText : c.dangerText };
  };

  const tone = (v) => {
    if (target == null || v == null) return c.textFaint;
    if (betterDir === 'neutral') return c.textMuted;
    const good = betterDir === 'up' ? v >= target : v <= target;
    return good ? c.successText : c.dangerText;
  };

  return (
    <div style={s.wrap}>
      <svg viewBox={`0 0 ${W} ${H}`} style={s.svg} role="img"
        preserveAspectRatio={stretch ? 'none' : undefined}
        aria-label={points.map((p) => `${p.label} ${fmt ? fmt(p.value) : p.value}`).join(', ')}>
        {legacyPts.length > 1 && (
          <path d={pathOf(legacyPts, 0)} fill="none" stroke={c.borderStrong} strokeWidth="1.5"
            strokeDasharray="3 3" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        )}
        {livePts.length > 1 && (
          <path d={pathOf(livePts, Math.max(firstLive, 0))} fill="none" stroke={c.textStrong}
            strokeWidth="1.5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        )}
        {points.map((p, i) => (
          <circle key={p.month} cx={x(i)} cy={y(p.value)} r={hover === i ? 3.2 : (i === points.length - 1 ? 3 : 1.8)}
            fill={hover === i ? c.textStrong : (i === points.length - 1 ? tone(p.value) : c.textFaint)} />
        ))}

        {/* A hit target per month, the full height of the chart and half the
            gap either side of its point. The points themselves are 1.8 units
            across — a target nobody can hit with a mouse — and a reader aiming
            at "August" is aiming at a column, not at a dot.

            mouseover/mouseout rather than mouseenter/mouseleave: React derives
            enter from over, and the test harness can only dispatch the real
            event (see tests/mount.jsx). Not focusable, deliberately — nine tab
            stops per chart across six charts would make the page's keyboard
            order useless, and the figures already reach a screen reader
            through the svg's aria-label, which lists every month and value. */}
        <g aria-hidden="true">
          {points.map((p, i) => {
            const left = i === 0 ? 0 : (x(i - 1) + x(i)) / 2;
            const right = i === points.length - 1 ? W : (x(i) + x(i + 1)) / 2;
            return (
              <rect key={p.month} data-spark-point={i}
                x={left} y={0} width={Math.max(right - left, 1)} height={H}
                fill="transparent" style={s.hit}
                onMouseOver={() => setHover(i)} onMouseOut={() => setHover(null)} />
            );
          })}
        </g>
      </svg>

      {hover != null && (() => {
        const p = points[hover];
        const d = change(hover);
        const pctOfWidth = (x(hover) / W) * 100;
        // Clamped by swapping the anchor rather than by measuring: the tooltip
        // has no width until it is laid out, and jsdom never lays anything
        // out. At the ends it hangs from the near edge instead of centring.
        const anchor = pctOfWidth < 22 ? 'translateX(0)'
          : pctOfWidth > 78 ? 'translateX(-100%)' : 'translateX(-50%)';
        return (
          <div role="status" style={{ ...s.tip, left: `${pctOfWidth}%`, transform: anchor }}>
            {/* The same two notes the Board puts on its bars, from the same
                field: a month from the old phone system, and the one month
                that is a partial — June holds from the 4th, when Genesys
                started, so its figure is not comparable with a whole month's
                and the reader should not have to know that separately. */}
            <span style={s.tipLabel}>
              {p.label}{p.legacy ? ' (InContact)' : p.partialFrom ? ' (from Jun 4)' : ''}
            </span>
            <span style={s.tipValue}>{fmt ? fmt(p.value) : p.value}</span>
            {d && (
              <span style={{ ...s.tipDelta, color: d.good == null ? c.textFaint : (d.good ? TIP_GOOD : TIP_BAD) }}>
                {d.pc >= 0 ? '+' : '−'}{Math.abs(d.pc).toFixed(1)}% vs {d.prevLabel}
              </span>
            )}
          </div>
        );
      })()}
      {markers && (
        <div style={s.markers} aria-hidden="true">
          {points.map((p, i) => {
            const d = step(i);
            return d && (
              <span key={p.month} style={{ ...s.marker, left: `${(x(i) / W) * 100}%`,
                top: `${Math.max(0, y(p.value) - 13).toFixed(1)}px`, color: d.color }}>{d.glyph}</span>
            );
          })}
        </div>
      )}
      {axis && (
        <div style={s.axis} aria-hidden="true">
          {points.map((p, i) => (
            <span key={p.month} title={p.label} style={{ ...s.axisTick, left: `${(x(i) / W) * 100}%` }}>
              {String(p.label || '').charAt(0)}
            </span>
          ))}
        </div>
      )}
      <div style={s.foot}>
        {/* Only where there IS a dashed half. The supporting metrics start in
            June — InContact reported none of them — and a legend for a line
            that is not drawn reads as a gap in the data. */}
        <span>{legacyPts.length > 1 ? 'dashed = InContact' : ''}</span>
        {rangeLabel && <span style={s.range}>Range: {rangeLabel}</span>}
      </div>
    </div>
  );
}

// The tooltip is a dark chip, so the semantic greens and reds from the theme —
// which are chosen for contrast against a WHITE card — are too dark to read on
// it. These are the same two meanings lightened for a dark surface, and they
// are the only place in this file that does not take a colour from the theme.
const TIP_GOOD = '#6EE7B7';
const TIP_BAD = '#FCA5A5';

const s = {
  wrap:     { marginTop: '8px', position: 'relative' },
  hit:      { cursor: 'default' },
  // Above the line and inside the card: the sparkline sits at the bottom of an
  // Exec card, so a tooltip placed under the cursor would fall outside it.
  // zIndex from the theme's scale for the same reason the filter menus take it
  // — this floats over a card that a sticky sidebar can otherwise paint over.
  tip:      { position: 'absolute', bottom: `${H + 6}px`, zIndex: z.menu,
    display: 'flex', alignItems: 'baseline', gap: '6px', whiteSpace: 'nowrap',
    background: '#0F172A', color: '#F8FAFC', borderRadius: '6px',
    padding: '4px 8px', fontSize: '11px', lineHeight: 1.3,
    boxShadow: '0 6px 18px rgba(15,23,42,0.28)', pointerEvents: 'none' },
  tipLabel: { color: '#CBD5E1' },
  tipValue: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  tipDelta: { fontVariantNumeric: 'tabular-nums' },
  markers:  { position: 'absolute', top: 0, left: 0, right: 0, height: `${H}px`, pointerEvents: 'none' },
  marker:   { position: 'absolute', transform: 'translateX(-50%)',
    fontSize: '9px', fontWeight: 750, lineHeight: 1 },
  axis:     { position: 'relative', height: '12px', marginTop: '2px' },
  axisTick: { position: 'absolute', transform: 'translateX(-50%)', fontSize: '10px',
    lineHeight: 1, color: c.textFaint },
  svg:      { width: '100%', height: '46px', display: 'block' },
  foot:     { display: 'flex', justifyContent: 'space-between', gap: '8px',
    fontSize: '10px', color: c.textFaint, marginTop: '2px' },
  range:    { fontVariantNumeric: 'tabular-nums' },
};
